import React, { useState, useEffect } from 'react';
import { Puestos } from '../../types';
import { HiOutlinePencilSquare, HiOutlineTrash } from 'react-icons/hi2';
import { FaCog, FaInbox } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  WorkstationOption,
  RenderCreatePuestosModal,
  RenderDeletePuestoModal,
  RenderUpdatePuestosModal
} from './RenderFunctions';
import { updatePuesto, DeletePuestosSelected } from '../../../../../crud/management_api/puestos';
import { getPermissionState } from '../../../../../utils/functions';
import './Table.scss';

interface TableProps {
  puestos: Puestos[];
  workstations: WorkstationOption[];
  setPuestos: React.Dispatch<React.SetStateAction<Puestos[]>>;
  showSelection: boolean;
  selectedPuestos: Set<string>;
  setSelectedPuestos: React.Dispatch<React.SetStateAction<Set<string>>>;
  showCreateModal: boolean;
  setShowCreateModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const Table: React.FC<TableProps> = ({ 
  puestos, 
  workstations, 
  setPuestos,
  showSelection: externalShowSelection,
  selectedPuestos: externalSelectedPuestos,
  setSelectedPuestos: externalSetSelectedPuestos,
  showCreateModal: externalShowCreateModal,
  setShowCreateModal: externalSetShowCreateModal,
}) => {
  const [currentData, setCurrentData] = useState<Puestos[]>(puestos);

  // Sincroniza currentData con los datos filtrados
  useEffect(() => {
    setCurrentData(puestos);
  }, [puestos]);
  
  // Use external state for selection
  const showSelection = externalShowSelection;
  const selectedPuestos = externalSelectedPuestos;
  const setSelectedPuestos = externalSetSelectedPuestos;
  const showCreateModal = externalShowCreateModal;
  const setShowCreateModal = externalSetShowCreateModal;
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentPuesto, setCurrentPuesto] = useState<Puestos | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  const canCreate = getPermissionState(44);
  const canEdit = getPermissionState(44);
  const canDelete = getPermissionState(44);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleShowDeleteModal = (puesto: Puestos) => {
    setCurrentPuesto(puesto);
    setShowDeleteModal(true);
  };

  const handleShowEditModal = async (puesto: Puestos) => {
    if (puesto) {
      setCurrentPuesto(puesto);
      setShowEditModal(true);
    } else {
      console.error('Failed to fetch puesto for editing.');
      toast.error('El puesto no puede ser editado');
    }
  };

  const toggleItemState = (id: string) => {
    updatePuesto(Number(id), { state: !currentData.find(p => p.puesto_id === id)?.state })
      .then(() => {
        toast.success('Estado del puesto se ha actualizado con éxito');
        setCurrentData(prev =>
          prev.map(p =>
            p.puesto_id === id ? { ...p, state: !p.state } : p
          )
        );
        setPuestos(prev =>
          prev.map(p =>
            p.puesto_id === id ? { ...p, state: !p.state } : p
          )
        );
      })
      .catch(error => {
        console.error('Error al actualizar el estado del puesto:', error);
        toast.error('Error al actualizar el estado del puesto');
      });
  }

  const handleCheckboxChange = (puesto_id: string) => {
    const newSet = new Set(selectedPuestos);
    newSet.has(puesto_id) ? newSet.delete(puesto_id) : newSet.add(puesto_id);
    setSelectedPuestos(newSet);
  };

  const handleDeleteMultiple = async () => {
    if (selectedPuestos.size === 0) {
      toast.error('Debe seleccionar al menos un puesto para eliminar');
      return;
    }
    const ids = Array.from(selectedPuestos);
    try {
      await DeletePuestosSelected(ids);
      toast.success('Puestos eliminados con éxito');
      setCurrentData(prev => prev.filter(p => !ids.includes(p.puesto_id)));
      setPuestos(prev => prev.filter(p => !ids.includes(p.puesto_id)));
      setSelectedPuestos(new Set());
    } catch (error) {
      console.error(error);
      toast.error('Error al eliminar los puestos');
    }
  }

  const getUsersQuantity = (puesto: Puestos): number => {
    const qtyObj = puesto.users?.find((u: any) => 'users_quantity' in u);
    return qtyObj ? (qtyObj as any).users_quantity : 0;
  };

  const renderTable = () => (
    <div className="mp-table-card">
      <table className="mp-table" style={{ tableLayout: 'fixed' }}>
        <thead className="mp-table-head">
          <tr>
            {showSelection && <th />}
            <th>Nombre</th>
            <th className="no-responsive">Descripción</th>
            <th>Estado</th>
            <th>{isMobile ? 'N° usuarios' : 'Cantidad de usuarios'}</th>
            <th className="no-responsive">{isMobile ? 'Estación' : 'Estación de Trabajo'}</th>
            <th>{isMobile ? <FaCog /> : 'Acciones'}</th>
          </tr>
        </thead>
        <tbody className="mp-table-body">
          {currentData.map(puesto => (
            <tr key={puesto.puesto_id}>
              {showSelection && (
                <td>
                  <div className="mp-radio-button">
                    <input
                      id={`checkbox-${puesto.puesto_id}`}
                      type="checkbox"
                      checked={selectedPuestos.has(puesto.puesto_id)}
                      onChange={() => handleCheckboxChange(puesto.puesto_id)}
                    />
                  </div>
                </td>
              )}
              <td>{puesto.puesto_name}</td>
              <td className="no-responsive">{puesto.puesto_description}</td>
              <td>
                <label className="switch" style={{ marginLeft: '20px', marginBottom: '20px' }}>
                  <input
                    type="checkbox"
                    checked={puesto.state}
                    onChange={() => toggleItemState(puesto.puesto_id)}
                  />
                  <span className="slider round" />
                </label>
              </td>
              <td>{getUsersQuantity(puesto)}</td>
              <td className="no-responsive">{puesto.workstation_name || 'No asignada'}</td>
              <td>
                <div className="flex items-center justify-center gap-1.5">
                  {canEdit && (
                    <button className="mp-btn-icon" title="Editar" onClick={() => handleShowEditModal(puesto)}>
                      <HiOutlinePencilSquare size={16} />
                    </button>
                  )}
                  {canDelete && (
                    <button className="mp-btn-icon mp-btn-icon-danger" title="Eliminar" onClick={() => handleShowDeleteModal(puesto)}>
                      <HiOutlineTrash size={16} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderNoData = () => (
    <div className="mp-table-card">
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <FaInbox size={48} className="opacity-30" />
        <p className="text-lg opacity-60">No has creado ningún puesto aún!</p>
        {canCreate && (
          <button className="mp-btn-primary" onClick={() => setShowCreateModal(true)}>
            Crear puesto
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div>
      {currentData.length > 0 ? renderTable() : renderNoData()}

      <RenderDeletePuestoModal
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        currentPuesto={currentPuesto}
        currentData={currentData}
        setCurrentData={(data: Puestos[]) => {
          setCurrentData(data);
          setPuestos(data);
        }}
      />

      <RenderCreatePuestosModal
        showCreateModal={showCreateModal}
        setShowCreateModal={setShowCreateModal}
        currentData={currentData}
        setCurrentData={(data: Puestos[]) => {
          setCurrentData(data);
          setPuestos(data);
        }}
      />

      <RenderUpdatePuestosModal
        showEditModal={showEditModal}
        setShowEditModal={setShowEditModal}
        setCurrentData={(data: Puestos[]) => {
          setCurrentData(data);
          setPuestos(data);
        }}
        currentData={currentData}
        currentPuesto={currentPuesto}
      />
    </div>
  );
};

export default Table;
